import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useMemo, useState } from 'react';
import { ChevronDown, Search, CreditCard, FileText, Download, Trash2,} from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import Barcode from '../../components/Barcode';
import { ForLocation } from './DeviceSetupPanels';
import { needsTerminalId, activeSheetFor, type VarSheet } from '@platform/shared-types';
import {
  merchantApi, type Filed, type MerchantApplicationView,
} from '../../services/merchantApi';
import { toastService } from '../../services/toastService';
import type { DeviceUnitRow, DeviceModelRow } from '../../services/hardwareApi';

/**
 * THE SCREEN THAT DECIDES WHICH SHEET PROGRAMS WHICH READER.
 *
 * One merchant application can hold VAR sheets from two sources — the
 * merchant's own and a payments partner's — and `activeSheetFor` (shared-types)
 * is the single rule for which one, if any, actually advances a given reader.
 * This panel is that rule made operable: one row per payment unit, reading
 * what it advances with today, and a custom chooser (never a native
 * `<select>`) for changing it.
 *
 * ONE SHEET, ONE READER is enforced server-side by the bind endpoint, which
 * stamps `supersededAt` on whatever the new binding replaces — nothing here
 * ever deletes a sheet. This panel mirrors that invariant in the chooser: a
 * sheet already bound to another reader is shown, never hidden, but disabled.
 */

type Sheet = Filed<VarSheet>;

interface Props {
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  varSheets: Sheet[];
  applicationId: string;
  tenantId: string;
  /** Whose store this is. A merchant application belongs to ONE location, and
   *  a tenant with four shops has four — binding a sheet without the business
   *  and branch on screen is how a reader gets keyed to the wrong one. */
  tenantName?: string;
  locationName?: string;
  locationId?: string;
  /** Handed the fresh application straight from the bind response — no refetch needed. */
  onChanged?: (app: MerchantApplicationView) => void;
}

const modelOf = (models: DeviceModelRow[], id?: string) => models.find((m) => m.modelId === id);

const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

/** Origin fields only — accepts both the `Filed<VarSheet>` list and the raw
    `VarSheet` `activeSheetFor` hands back, so neither caller needs a cast. */
type SheetOrigin = Pick<VarSheet, 'uploadedByRole' | 'uploadedBy'>;

/** Short badge for a sheet's origin — matches the mockup's ISO/Merchant/Admin tags. */
const originTag = (v: SheetOrigin): string =>
  v.uploadedByRole === 'iso' ? 'ISO' : v.uploadedByRole === 'super_admin' ? 'Admin' : 'Merchant';

/** The fuller line used on the row itself. */
const originLine = (v: SheetOrigin): { label: string; cls: string } =>
  v.uploadedByRole === 'iso'
    ? { label: v.uploadedBy ? `From ${v.uploadedBy}` : 'From the payments partner', cls: 'bg-slate-200 text-slate-700' }
    : v.uploadedByRole === 'super_admin'
      ? { label: 'From RubyTill', cls: 'bg-slate-200 text-slate-700' }
      : { label: 'From the merchant', cls: 'bg-amber-100 text-amber-700' };

const maskedMid = (mid?: string) => (mid ? `MID ${mid}` : '');

/** The partner's proposed rate, when they recorded one against the sheet. */
const sheetRates = (sheet: any): any | undefined => {
  const r = sheet?.rates;
  if (!r) return undefined;
  return (r.credit || r.debit || r.note) ? r : undefined;
};

/** "2.2% + 10¢", or a dash when that line says nothing. */
const rateText = (line?: { percent?: number; perTransactionCents?: number }) => {
  const bits: string[] = [];
  if (typeof line?.percent === 'number') bits.push(`${line.percent}%`);
  if (typeof line?.perTransactionCents === 'number') bits.push(`${line.perTransactionCents}¢`);
  return bits.join(' + ');
};

/**
 * One labelled fact. A label above a value is what makes a MID findable in a
 * row that also carries a processor, two rates and a file.
 */
const Field: React.FC<{ label: string; value?: string; note?: string; mono?: boolean }> = ({
  label, value, note, mono,
}) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-stone-400 mb-1">{label}</p>
    <p className={`text-[12.5px] truncate ${value ? 'font-semibold text-stone-900' : 'text-stone-400'} ${mono ? 'font-mono' : ''}`}>
      {value || '—'}
    </p>
    {note && <p className="text-[11px] text-stone-500 mt-0.5 leading-snug">{note}</p>}
  </div>
);

/** 20 MB — a VAR sheet is a PDF of a page or two, never a scan of a filing cabinet. */
const MAX_VAR_BYTES = 20 * 1024 * 1024;

const centsToRate = (v: string) => {
  const n = parseFloat(v.replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : undefined;
};

/**
 * THE RATE, ASKED FOR AT THE MOMENT IT IS KNOWN.
 *
 * A partner uploading their own sheet through the portal is prompted for the
 * rates they are quoting. A sheet that arrives as an email attachment skips
 * that entirely — so the document lands with no rate beside it, and the
 * number nobody recorded is the one the merchant asks about later.
 *
 * Shown INLINE straight after an upload, without a page refresh, and only
 * when the sheet has no rates: if the partner already entered them there is
 * nothing to ask and asking anyway invites someone to overwrite good data
 * with a half-remembered figure.
 *
 * DELIBERATELY NOT A PRICE ENGINE — nothing computes with these. It is the
 * partner's own summary of the schedule, kept beside the sheet it came from,
 * which is why the authoritative document is still the file itself.
 */
const RatePrompt: React.FC<{
  varId: string;
  fileName?: string;
  saving: boolean;
  onSave: (rates: { credit?: any; debit?: any; note?: string }) => void;
  onSkip: () => void;
}> = ({ varId, fileName, saving, onSave, onSkip }) => {
  const [creditPct, setCreditPct] = useState('');
  const [creditCents, setCreditCents] = useState('');
  const [debitPct, setDebitPct] = useState('');
  const [debitCents, setDebitCents] = useState('');
  const [note, setNote] = useState('');

  const anything = [creditPct, creditCents, debitPct, debitCents, note].some((v) => v.trim());

  const line = (pct: string, cents: string) => {
    const percent = pct.trim() ? centsToRate(pct) : undefined;
    const per = cents.trim() ? Math.round(centsToRate(cents) ?? 0) : undefined;
    return percent === undefined && per === undefined
      ? undefined
      : { ...(percent !== undefined ? { percent } : {}), ...(per !== undefined ? { perTransactionCents: per } : {}) };
  };

  const Row: React.FC<{
    label: string; pct: string; setPct: (v: string) => void; cents: string; setCents: (v: string) => void;
  }> = ({ label, pct, setPct, cents, setCents }) => (
    <div className="flex items-center gap-2">
      <span className="w-14 shrink-0 text-[11.5px] font-semibold text-stone-600">{label}</span>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <input
          value={pct} onChange={(e) => setPct(e.target.value)}
          inputMode="decimal" placeholder="2.2"
          className="w-full min-w-0 rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-right font-mono text-[12px] outline-none focus:border-[#DC143C]"
        />
        <span className="shrink-0 text-[11.5px] text-stone-500">%</span>
      </div>
      <span className="shrink-0 text-[11.5px] text-stone-400">+</span>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <input
          value={cents} onChange={(e) => setCents(e.target.value)}
          inputMode="numeric" placeholder="10"
          className="w-full min-w-0 rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-right font-mono text-[12px] outline-none focus:border-[#DC143C]"
        />
        <span className="shrink-0 text-[11.5px] text-stone-500">&cent;</span>
      </div>
    </div>
  );

  return (
    <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <p className="mb-0.5 text-[12.5px] font-semibold text-amber-900">
        Add the rate for this sheet?
      </p>
      <p className="mb-2.5 text-[11px] leading-snug text-amber-800">
        The partner didn&rsquo;t record one on {fileName || 'this sheet'}. Enter it now while the
        document is in front of you &mdash; it sits beside the sheet, and nothing is calculated from it.
      </p>

      <div className="space-y-2">
        <Row label="Credit" pct={creditPct} setPct={setCreditPct} cents={creditCents} setCents={setCreditCents} />
        <Row label="Debit" pct={debitPct} setPct={setDebitPct} cents={debitCents} setCents={setDebitCents} />
        <input
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Monthly fee, term, anything that makes those numbers mean something"
          className="w-full rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[12px] outline-none focus:border-[#DC143C]"
        />
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          disabled={saving || !anything}
          onClick={() => onSave({
            credit: line(creditPct, creditCents),
            debit: line(debitPct, debitCents),
            note: note.trim() || undefined,
          })}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-[#DC143C] px-3 text-[12px] font-semibold text-white disabled:opacity-40"
        >
          {saving ? <RubyGemMark size={12} drawing color="currentColor" /> : null} Save rate
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={saving}
          className="min-h-[36px] rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-600 disabled:opacity-40"
        >
          Not now
        </button>
        <span className="ml-auto font-mono text-[10.5px] text-amber-700">{varId}</span>
      </div>
    </div>
  );
};

const VarSheetBindingPanel: React.FC<Props> = ({
  units, models, varSheets, applicationId, tenantId, onChanged,
  tenantName, locationName, locationId,
}) => {
  /* Payment units only — a receipt printer has no MID to advance with and
     must never show up asking for one. Same predicate the dispatch gate and
     MerchantPanel already use, so this screen can never disagree with them
     about what counts as a card reader. */
  const paymentUnits = useMemo(() => {
    const byId = new Map(models.map((m) => [m.modelId, m]));
    return units.filter((u) => needsTerminalId(byId.get(u.modelId) as any));
  }, [units, models]);

  const [openFor, setOpenFor] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  /* A CUSTOM confirm, never `window.confirm` — platform rule. */
  const [confirmUnbind, setConfirmUnbind] = useState<{ varId: string; name: string; rta: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ varId: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  /* Set the moment an upload lands, so the rate question appears in place
     rather than after a refresh nobody performs. */
  const [rateFor, setRateFor] = useState<{ varId: string; fileName?: string } | null>(null);
  const [savingRates, setSavingRates] = useState(false);
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  /**
   * Open the bound sheet. The URL is minted per click and never cached — the
   * URL IS the permission and it expires in minutes.
   */
  const openSheet = async (varId: string) => {
    setBusy(`file:${varId}`);
    try {
      const { url } = await merchantApi.fileUrl(tenantId, applicationId, 'var', varId);
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not open that sheet.');
    } finally {
      setBusy(null);
    }
  };

  const unbind = async () => {
    if (!confirmUnbind) return;
    setBusy(`unbind:${confirmUnbind.varId}`);
    try {
      const { application } = await merchantApi.unbindVarSheet(tenantId, applicationId, confirmUnbind.varId);
      onChanged?.(application);
      setConfirmUnbind(null);
      toastService.success('Sheet removed. This reader no longer advances with it.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not remove this sheet.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * PUT THE SHEET IN OURSELVES.
   *
   * Three steps, in this order, because the storage key has to name the
   * record it belongs to: mint the id here, presign a PUT against it, upload
   * straight to S3, then record the sheet. Minting client-side is what lets
   * the key and the record agree without a round trip in between.
   *
   * NO MID IS ASKED FOR. The operator is reading a PDF that was emailed to
   * them and often has not found it yet; the service allows a blank one and
   * binding asks again. Holding the document outside the system over a field
   * that can be typed a minute later is the problem this solves, not a
   * standard worth keeping.
   */
  const uploadSheet = async (file: File) => {
    if (file.size > MAX_VAR_BYTES) {
      toastService.error(`${file.name} is over 20 MB — that is too big for a VAR sheet.`);
      return;
    }
    setUploading(true);
    try {
      const varId = `var_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      /*
        NORMALISED THE WAY THE SERVER NORMALISES IT, and then used for both
        calls. `presignPut` signs `contentType.split(';')[0].trim()
        .toLowerCase()`, so a browser reporting `application/PDF` or a type
        carrying `; charset=` would be signed as one string and sent as
        another — a signature mismatch, and another silent 403.
      */
      const type = (file.type || 'application/pdf').split(';')[0].trim().toLowerCase();
      const { url, storageKey } = await merchantApi.uploadUrl(
        tenantId, applicationId, 'var', varId, file.name, type,
      );
      /*
        EVERY SIGNED HEADER MUST BE SENT BACK, OR S3 RETURNS 403.

        `presignPut` signs the object with `ServerSideEncryption: 'AES256'`, so
        the SDK puts `x-amz-server-side-encryption` into the URL's
        `X-Amz-SignedHeaders`. A PUT that omits it produces a signature S3
        cannot reproduce, and the 403 that comes back reads in the browser like
        a permissions or CORS problem rather than a missing header — which is
        exactly how this hid. The content type is pinned for the same reason:
        sending a different one is the same 403.

        Both headers are also what `platform-app`'s uploader and
        `AgreementUploadModal` already send against this same presign.
      */
      const put = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': type,
          'x-amz-server-side-encryption': 'AES256',
        },
        body: file,
      });
      if (!put.ok) throw new Error(`the upload was refused (${put.status})`);

      const res = await merchantApi.upsertVarSheet(tenantId, applicationId, {
        storageKey, fileName: file.name.slice(0, 255),
      });
      onChanged?.(res.application);
      toastService.success('Sheet added. Choose the reader it programs.');
      /* Straight into the rate question, in place — the document is on screen
         and this is the only moment somebody has it open. */
      setRateFor({ varId: res.varId, fileName: file.name });
    } catch (e: any) {
      toastService.error(e?.message || 'Could not upload that sheet.');
    } finally {
      setUploading(false);
    }
  };

  const saveRates = async (rates: { credit?: any; debit?: any; note?: string }) => {
    if (!rateFor) return;
    setSavingRates(true);
    try {
      const res = await merchantApi.upsertVarSheet(tenantId, applicationId, { varId: rateFor.varId, rates });
      onChanged?.(res.application);
      setRateFor(null);
      toastService.success('Rate saved against the sheet.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not save the rate.');
    } finally {
      setSavingRates(false);
    }
  };

  const removeSheet = async () => {
    if (!confirmDelete) return;
    setBusy(`delete:${confirmDelete.varId}`);
    try {
      const { application } = await merchantApi.deleteVarSheet(tenantId, applicationId, confirmDelete.varId);
      onChanged?.(application);
      setConfirmDelete(null);
      toastService.success('Sheet deleted.');
    } catch (e: any) {
      /* The service refuses a sheet that is bound or was keyed from, and says
         why — including what to do instead. Its words, not a paraphrase. */
      toastService.error(e?.message || 'Could not delete that sheet.');
    } finally {
      setBusy(null);
    }
  };

  const openChooser = (unitId: string) => {
    setOpenFor((cur) => (cur === unitId ? null : unitId));
    setSearch('');
  };

  const bind = async (varId: string, unitId: string) => {
    setBusy(varId);
    try {
      const { application } = await merchantApi.bindVarSheet(tenantId, applicationId, varId, unitId);
      onChanged?.(application);
      setOpenFor(null);
      setSearch('');
      toastService.success('Sheet bound. This reader now advances with it.');
    } catch (e: any) {
      // The service's own words — "One sheet, one reader" on a 409, "No such
      // VAR sheet" on a 404 — are written for this reader, not paraphrased.
      toastService.error(e?.message || 'Could not bind this sheet.');
    } finally {
      setBusy(null);
    }
  };

  if (paymentUnits.length === 0) {
    return (
      <p className="text-[12.5px] text-stone-500">
        Nothing on this order needs a VAR sheet — no card readers are on it.
      </p>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[13px] font-bold text-stone-900">VAR sheets → readers</span>
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] text-stone-500">
            {paymentUnits.length} reader{paymentUnits.length === 1 ? '' : 's'} · {varSheets.filter((v) => v.supersededAt === undefined).length} sheet{varSheets.filter((v) => v.supersededAt === undefined).length === 1 ? '' : 's'} on file
          </span>
          {/*
            THE WAY IN FOR A SHEET THAT ARRIVED BY EMAIL.

            The partner uploads their own through the portal and usually does;
            when they attach it to a message instead, this is what stops the
            order waiting on a document that is already in somebody's inbox.
          */}
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadSheet(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C] disabled:opacity-40"
          >
            {uploading
              ? <><RubyGemMark size={12} drawing color="currentColor" /> Uploading</>
              : <><FileText size={12} /> Upload a sheet</>}
          </button>
        </div>
      </div>

      <div className="px-5 py-4 flex flex-col gap-2">
        {/* Whose store, and which branch — the same header the device panels
            carry, from the same component so the two cannot word it differently. */}
        <ForLocation
          tenantName={tenantName}
          locationName={locationName}
          locationId={locationId}
          what="VAR sheets"
        />

        {/* Straight after an upload, in place — no refresh, and only when the
            partner left the rate blank. */}
        {rateFor && (
          <RatePrompt
            varId={rateFor.varId}
            fileName={rateFor.fileName}
            saving={savingRates}
            onSave={saveRates}
            onSkip={() => setRateFor(null)}
          />
        )}
        {paymentUnits.map((u) => {
          const model = modelOf(models, u.modelId);
          /* THE DEVICE, NOT ITS UUID. `unit_a9bfd942-…` identifies the row in
             DynamoDB and nothing a person can pick off a shelf. The tracking id
             is what is printed on the box, so it leads. */
          const rta = u.trackingId || u.serialNumber || u.unitId;
          /* The rule still comes from `activeSheetFor` and only from there.
             This recovers the FILED row it picked — `hasFile` lives on the
             wrapper the API adds, not on the bare VarSheet the rule is typed
             against, and the open button needs to know whether there is a
             document at all. */
          const active = activeSheetFor(varSheets, u.unitId);
          const sheet = active ? varSheets.find((v) => v.varId === active.varId) : undefined;
          /* Sheets that used to program THIS reader before a replacement was
             bound — kept, never hidden, struck through with who put them
             there and when they stopped being in force. */
          const history = varSheets
            .filter((v) => v.unitId === u.unitId && v.supersededAt !== undefined)
            .sort((a, b) => (b.supersededAt || 0) - (a.supersededAt || 0));

          const isOpen = openFor === u.unitId;

          /* Candidates for the chooser: every live sheet except the one
             already advancing this reader. A sheet bound to another reader
             stays in the list — shown, not hidden — but cannot be picked. */
          const candidates = varSheets.filter(
            (v) => v.supersededAt === undefined && v.varId !== sheet?.varId,
          );
          const filtered = candidates.filter((v) => {
            if (!search.trim()) return true;
            const hay = `${v.fileName || ''} ${v.mid || ''} ${v.uploadedBy || ''}`.toLowerCase();
            return hay.includes(search.trim().toLowerCase());
          });

          return (
            <div
              key={u.unitId}
              className={`rounded-xl px-4 py-3 ${
                isOpen
                  ? 'border-2'
                  : sheet
                    ? 'border border-stone-200'
                    : 'border border-red-200 bg-red-50/40'
              }`}
              style={isOpen ? { borderColor: '#DC143C' } : undefined}
            >
              {/*
                TWO PARTS, NOT ONE PILE.

                Everything — the device, its barcode, the bound file, the MID,
                the processor and the rates — used to live in one nested
                column inside a flex row, so the MID ended up as grey text at
                the end of a wrapping line and the rates below it. The header
                now says WHICH READER and WHAT STATE it is in; the grid under
                it says WHAT IT IS BOUND TO, one labelled field at a time.
              */}

              {/* ── which reader, and where it stands ───────────────────── */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex min-w-0 items-center gap-3">
                  <ShimmerImage
                    src={model?.media?.heroImageUrl}
                    alt={model?.modelName || rta}
                    width={44}
                    height={44}
                    objectFit="contain"
                    className="shrink-0 rounded-lg border border-stone-200"
                    fallbackBgColor="bg-stone-100"
                    fallbackIcon={<CreditCard size={17} className="text-stone-400" />}
                  />
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-stone-900">
                      {model?.modelName || u.modelId}
                    </p>
                    <p className="mt-0.5 font-mono text-[11.5px] text-stone-500">
                      {rta}
                      {u.serialNumber && u.serialNumber !== rta ? ` · ${u.serialNumber}` : ''}
                    </p>
                    {/* aria-hidden inside Barcode — the value is already above
                        it as text, so a reader would announce it twice. */}
                    <div className="mt-1 text-stone-700">
                      <Barcode value={rta} height={20} moduleWidth={1.2} />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  {isOpen ? (
                    <span className="px-2 py-1 rounded-lg text-[11px] font-bold bg-rose-100 text-rose-700">
                      Choosing a sheet…
                    </span>
                  ) : sheet ? (
                    <>
                      <span className="px-2 py-1 rounded-lg text-[11px] font-bold bg-emerald-100 text-emerald-700">
                        Advancing
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10.5px] font-semibold ${originLine(sheet).cls}`}>
                        {originLine(sheet).label}
                      </span>
                    </>
                  ) : (
                    <span className="px-2 py-1 rounded-lg text-[11px] font-bold bg-red-100 text-red-700">
                      Nothing bound
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => openChooser(u.unitId)}
                    className="min-h-[44px] px-3.5 py-2.5 rounded-xl border border-stone-300 bg-white text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50 flex items-center gap-1.5"
                  >
                    {sheet ? 'Change sheet' : 'Choose a sheet'}
                    <ChevronDown size={14} className="text-stone-400" />
                  </button>
                  {/*
                    REMOVE, not delete. The sheet stays on the application and
                    goes back in the pool; only the binding goes. A statement
                    bound by mistake is the ordinary reason, and destroying the
                    document over a mis-click would destroy evidence.
                  */}
                  {!isOpen && sheet && (
                    <button
                      type="button"
                      onClick={() => setConfirmUnbind({ varId: sheet.varId, name: sheet.fileName || sheet.varId, rta })}
                      title="Take this sheet off this reader"
                      aria-label="Take this sheet off this reader"
                      className="inline-flex min-h-[44px] w-11 items-center justify-center rounded-xl border border-stone-300 bg-white text-stone-500 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* ── what it is bound to, one labelled field at a time ───── */}
              {!isOpen && (sheet ? (
                <div className="mt-3 pt-3 border-t border-stone-200/70 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
                  <Field label="MID" value={sheet.mid} mono />
                  <Field label="Processor" value={sheet.processor} />
                  <Field label="Credit" value={rateText(sheetRates(sheet)?.credit)} note={sheetRates(sheet)?.credit?.note} />
                  <Field label="Debit" value={rateText(sheetRates(sheet)?.debit)} note={sheetRates(sheet)?.debit?.note} />

                  <div className="col-span-2 md:col-span-4">
                    <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-stone-400 mb-1">The sheet</p>
                    <button
                      type="button"
                      onClick={() => openSheet(sheet.varId)}
                      disabled={!sheet.hasFile || busy === `file:${sheet.varId}`}
                      title={sheet.hasFile ? 'Open this sheet' : 'No file was uploaded with this sheet'}
                      className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-2.5 text-[11.5px] font-semibold text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy === `file:${sheet.varId}`
                        ? <RubyGemMark size={12} drawing color="currentColor" />
                        : <FileText size={12} className="text-rose-500" />}
                      <span className="max-w-[20rem] truncate">
                        {sheet.hasFile ? (sheet.fileName || sheet.varId) : 'No file was uploaded with this sheet'}
                      </span>
                      {sheet.hasFile && <Download size={11} className="text-stone-400" />}
                    </button>
                  </div>

                  {sheetRates(sheet)?.note && (
                    <div className="col-span-2 md:col-span-4">
                      <p className="text-[10px] uppercase tracking-[0.1em] font-bold text-stone-400 mb-1">Terms</p>
                      <p className="text-[12px] text-stone-700 leading-relaxed">{sheetRates(sheet)?.note}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-[11.5px] text-red-700 font-medium">
                  This reader does not advance until a sheet is bound to it.
                </p>
              ))}

              {/* HISTORY — never hidden, never deleted, struck through. */}
              {!isOpen && history.length > 0 && (
                <div className="mt-3 pt-3 border-t border-dashed border-stone-200 flex flex-col gap-1.5">
                  {history.map((h) => (
                    <div key={h.varId} className="flex items-center gap-2 opacity-60">
                      <div className="min-w-0 flex-1">
                        <p className="text-[11.5px] text-stone-500 line-through truncate">
                          {h.fileName || h.varId}{h.mid ? ` · ${maskedMid(h.mid)}` : ''}
                        </p>
                        <p className="text-[11px] text-stone-400">
                          {originLine(h).label} · superseded {when(h.supersededAt)}
                          {h.uploadedBy ? ` · uploaded by ${h.uploadedBy}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* THE CUSTOM CHOOSER — never a native <select>. Every sheet not
                  superseded is shown; one bound elsewhere is disabled, not
                  hidden, so the operator sees where it went. */}
              {isOpen && (
                <div className="mt-3 rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-3 py-2 border-b border-stone-100 flex items-center gap-2">
                    <Search size={14} className="text-stone-300 shrink-0" />
                    <input
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search sheets…"
                      aria-label={`Search sheets for ${u.unitId}`}
                      className="w-full text-[12.5px] min-h-[38px] outline-none placeholder:text-stone-300"
                    />
                  </div>

                  {filtered.length === 0 && (
                    <p className="px-3 py-3 text-[12px] text-stone-400">No sheets match.</p>
                  )}

                  {filtered.map((v) => {
                    const boundElsewhere = !!v.unitId && v.unitId !== u.unitId;
                    return (
                      /* A ROW, not a button. The delete control is a button of
                         its own and a button cannot be nested inside one, so
                         the choosing half is the button and they sit side by
                         side. */
                      <div
                        key={v.varId}
                        className={`flex items-center gap-1 border-b border-stone-50 last:border-0 ${
                          boundElsewhere ? 'opacity-50' : 'hover:bg-stone-50'
                        }`}
                      >
                        <button
                          type="button"
                          disabled={boundElsewhere || busy === v.varId}
                          onClick={() => bind(v.varId, u.unitId)}
                          title={boundElsewhere ? `Already programming ${v.unitId}` : undefined}
                          className={`min-w-0 flex-1 text-left px-3 py-2.5 flex items-center gap-3 min-h-[44px] ${
                            boundElsewhere ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        >
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: boundElsewhere ? '#d6d3d1' : '#DC143C' }}
                          />
                          <span className="min-w-0 flex-1">
                            <span className={`block text-[12.5px] font-semibold ${boundElsewhere ? 'text-stone-600' : 'text-stone-800'}`}>
                              {v.fileName || v.varId}
                            </span>
                            <span className={`block text-[11px] ${boundElsewhere ? 'text-stone-400' : 'text-stone-500'}`}>
                              {boundElsewhere
                                ? `Already programming ${v.unitId}`
                                : `${originLine(v).label}${v.mid ? ` · ${maskedMid(v.mid)}` : ''}`}
                            </span>
                          </span>
                          <span className={`px-2 py-0.5 rounded text-[10.5px] font-semibold shrink-0 ${
                            v.uploadedByRole === 'iso' || v.uploadedByRole === 'super_admin'
                              ? 'bg-slate-200 text-slate-700'
                              : 'bg-amber-100 text-amber-700'}`}
                          >
                            {originTag(v)}
                          </span>
                        </button>
                        {/* Offered only on a sheet bound to nothing — the one
                            the service will actually let go. */}
                        {!v.unitId && (
                          <button
                            type="button"
                            onClick={() => setConfirmDelete({ varId: v.varId, name: v.fileName || v.varId })}
                            title="Delete this sheet"
                            aria-label={`Delete ${v.fileName || v.varId}`}
                            className="mr-2 shrink-0 rounded-lg p-2 text-stone-400 hover:bg-white hover:text-red-600"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  <p className="px-3 py-2 text-[11px] text-stone-400 bg-stone-50">
                    One sheet, one reader. A sheet already programming another device is shown but not selectable.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmUnbind && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setConfirmUnbind(null)}
        >
          <div
            className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-stone-200 px-5 py-4">
              <h3 className="text-[15px] font-bold text-stone-900">Take this sheet off the reader?</h3>
              <p className="mt-1 text-[12.5px] text-stone-500">
                <span className="font-mono">{confirmUnbind.rta}</span> · {confirmUnbind.name}
              </p>
            </div>
            <div className="px-5 py-4 text-[12.5px] leading-relaxed text-stone-600">
              The sheet stays on this application and goes back in the pool, so you can bind it to a
              different reader or bind the right sheet to this one. <b className="text-stone-800">Nothing is
              deleted.</b>
              <span className="mt-2 block text-stone-500">
                This reader stops advancing until something is bound to it, and any terminal id keyed
                off this sheet is removed with it — a TID exists because a sheet issued it.
              </span>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
              <button
                onClick={() => setConfirmUnbind(null)}
                className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={unbind}
                disabled={busy === `unbind:${confirmUnbind.varId}`}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy === `unbind:${confirmUnbind.varId}` && <RubyGemMark size={13} drawing color="currentColor" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        DELETE, for the wrong PDF caught straight away.

        Worded to be clearly different from Remove above: that one takes a
        sheet off a reader and keeps it, this one takes it off the record. The
        service refuses it once the sheet is bound or a terminal was keyed
        from it, so the destructive case simply cannot be reached from here.
      */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-stone-200 px-5 py-4">
              <h3 className="text-[15px] font-bold text-stone-900">Delete this sheet?</h3>
              <p className="mt-1 text-[12.5px] text-stone-500">{confirmDelete.name}</p>
            </div>
            <div className="px-5 py-4 text-[12.5px] leading-relaxed text-stone-600">
              It comes off this application entirely &mdash; for the wrong file, or the wrong
              merchant&rsquo;s sheet.
              <span className="mt-2 block text-stone-500">
                A sheet that is bound to a reader, or that a terminal was keyed from, cannot be
                deleted: that one is the record of how the reader was set up. Bind a replacement
                instead and it is superseded.
              </span>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
              <button
                onClick={() => setConfirmDelete(null)}
                className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={removeSheet}
                disabled={busy === `delete:${confirmDelete.varId}`}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy === `delete:${confirmDelete.varId}` && <RubyGemMark size={13} drawing color="currentColor" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VarSheetBindingPanel;
